import type { ReactNode } from 'react';

import {
  BoutonImprimer,
  DataTable,
  Montant,
  Nombre,
  Badge,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { LIBELLE_CANAL, type Canal, type FiltreCanal, type ProduitGroupe } from './types.ts';

/** Libellé d'un canal reçu du serveur, tel quel s'il est inconnu. */
function libelleCanal(canal: string): string {
  return canal in LIBELLE_CANAL ? LIBELLE_CANAL[canal as Canal] : canal;
}

/**
 * Liste du catalogue.
 *
 * Deux « seuils » cohabitent dans ce domaine, et les confondre serait grave :
 *
 * - la QUANTITÉ MINIMALE du canal (RG-034), qui interdit de vendre au gros
 *   sous un certain nombre ;
 * - le SEUIL DE RÉAPPROVISIONNEMENT (RG-186), qui déclenche une alerte de
 *   stock.
 *
 * Ils portent donc des libellés explicites, jamais « seuil » tout court.
 */
export function ListeProduits({
  produits,
  canal,
  chargerTout,
  avecCouts,
  actions,
}: {
  /** Lignes de la PAGE affichée. */
  produits: ProduitGroupe[];
  canal: FiltreCanal;
  /**
   * Toute la liste filtrée, toutes pages confondues : c'est elle que le
   * bouton Imprimer met dans le PDF, pas seulement la page à l'écran.
   */
  chargerTout: () => Promise<ProduitGroupe[]>;
  /** Vrai si le serveur a envoyé les coûts — RG-143. */
  avecCouts: boolean;
  actions: (produit: ProduitGroupe) => ReactNode;
}): ReactNode {
  const { ouvrir } = useApercuPdf();
  // Avec un seul canal affiché, l'étiquette serait redondante avec le filtre.
  const etiquettes = canal === 'TOUS';
  const colonnes: Colonne<ProduitGroupe>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      rendu: (produit) => <span className="amount">{produit.reference}</span>,
      texte: (produit) => produit.reference,
      valeur: (produit) => produit.reference,
    },
    {
      cle: 'designation',
      entete: 'Désignation',
      rendu: (produit) => produit.designation,
      texte: (produit) => produit.designation,
      valeur: (produit) => produit.designation,
    },
    {
      cle: 'categorie',
      entete: 'Catégorie',
      rendu: (produit) => produit.categorieLibelle,
      texte: (produit) => produit.categorieLibelle,
      valeur: (produit) => produit.categorieLibelle,
    },
    {
      cle: 'prix',
      entete: 'Prix',
      numerique: true,
      // Un prix par canal, l'un sous l'autre : le produit ne s'affiche
      // qu'UNE fois, quel que soit le nombre de canaux où il se vend.
      rendu: (produit) => (
        <>
          {produit.canaux.map((entree) => (
            <div key={entree.canal}>
              {etiquettes && <span className="muted">{libelleCanal(entree.canal)} </span>}
              {entree.prixUnitaire === null ? (
                <Badge statut="en_attente" libelle="Sans prix" />
              ) : (
                <Montant valeur={entree.prixUnitaire} />
              )}
            </div>
          ))}
        </>
      ),
      texte: (produit) =>
        produit.canaux
          .map(
            (entree) =>
              (etiquettes ? `${libelleCanal(entree.canal)} ` : '') +
              (entree.prixUnitaire === null ? 'Sans prix' : formaterFcfa(entree.prixUnitaire)),
          )
          .join(' / '),
      // Sans prix trié EN PREMIER (-1) : c'est la fiche qui réclame une
      // action, elle ne doit pas se perdre au milieu du reste.
      valeur: (produit) =>
        produit.canaux.some((entree) => entree.prixUnitaire === null)
          ? -1
          : Math.min(...produit.canaux.map((entree) => entree.prixUnitaire ?? 0)),
    },
    {
      cle: 'quantite_minimale',
      entete: 'Qté min.',
      numerique: true,
      rendu: (produit) => (
        <>
          {produit.canaux.map((entree) => (
            <div key={entree.canal}>
              {etiquettes && <span className="muted">{libelleCanal(entree.canal)} </span>}
              <Nombre valeur={entree.quantiteMinimale} />
            </div>
          ))}
        </>
      ),
      texte: (produit) =>
        produit.canaux
          .map(
            (entree) =>
              (etiquettes ? `${libelleCanal(entree.canal)} ` : '') + String(entree.quantiteMinimale),
          )
          .join(' / '),
      valeur: (produit) => Math.min(...produit.canaux.map((entree) => entree.quantiteMinimale)),
    },
    {
      cle: 'stock',
      entete: 'Stock',
      numerique: true,
      rendu: (produit) => <CelluleStock produit={produit} />,
      texte: (produit) => String(produit.stockDisponible),
      valeur: (produit) => produit.stockDisponible,
    },
    {
      cle: 'seuil',
      entete: 'Seuil réappro.',
      numerique: true,
      rendu: (produit) =>
        produit.seuilReapprovisionnement === undefined ? (
          // « — » et non « 0 » : aucun seuil n'est pas un seuil à zéro.
          <span className="muted">—</span>
        ) : (
          <Nombre valeur={produit.seuilReapprovisionnement} />
        ),
      texte: (produit) =>
        produit.seuilReapprovisionnement === undefined
          ? '—'
          : String(produit.seuilReapprovisionnement),
      valeur: (produit) => produit.seuilReapprovisionnement ?? -1,
    },
    // Le coût n'apparaît QUE si le serveur l'a envoyé — RG-143.
    ...(avecCouts
      ? [
          {
            cle: 'cout',
            entete: 'Coût moyen',
            numerique: true,
            rendu: (produit: ProduitGroupe) => <Montant valeur={produit.coutMoyen ?? null} />,
            texte: (produit: ProduitGroupe) =>
              produit.coutMoyen === undefined ? '—' : formaterFcfa(produit.coutMoyen),
            valeur: (produit: ProduitGroupe) => produit.coutMoyen ?? -1,
          },
        ]
      : []),
    {
      cle: 'actions',
      entete: '',
      // `ligne--nowrap` plutôt que `.ligne` seule : sur mobile, faute de
      // place, les 5 boutons s'empilaient verticalement — et comme toutes
      // les cellules d'une ligne de tableau partagent sa hauteur, ça
      // gonflait TOUTE la ligne, y compris les colonnes visibles à gauche
      // avant même de faire défiler. Constaté le 15/10/2026.
      rendu: (produit) => <div className="ligne ligne--nowrap">{actions(produit)}</div>,
      // Pas de `texte` : une colonne d'actions n'a rien à imprimer.
    },
  ];

  return (
    <>
      {produits.length > 0 && (
        <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <BoutonImprimer
            onClick={() => {
              // Lit TOUTES les pages au moment du clic : 150 produits filtrés,
              // 150 lignes dans le PDF, même si l'écran n'en montre que 50.
              ouvrir(async () =>
                exporterTableauPdf({
                  titre: 'Produits',
                  sousTitre: `Canal : ${canal === 'TOUS' ? 'Tous les canaux' : LIBELLE_CANAL[canal]}`,
                  colonnes,
                  lignes: await chargerTout(),
                  nomFichier: 'produits',
                }),
              );
            }}
          />
        </div>
      )}
      <DataTable
        lignes={produits}
        cleDe={(produit) => produit.id}
        vide={
          canal === 'TOUS'
            ? 'Aucun produit.'
            : `Aucun produit vendable au ${LIBELLE_CANAL[canal].toLowerCase()}.`
        }
        colonnes={colonnes}
        // Le serveur pagine déjà par RÉFÉRENCE : ce tri par défaut ne fait que
        // l'afficher dans l'en-tête. Un clic sur un autre en-tête trie
        // seulement la page affichée, pas toute la liste.
        triParDefaut={{ cle: 'reference' }}
      />
    </>
  );
}

/**
 * Stock, avec ce qu'il faut pour le lire.
 *
 * Trois cas distincts : jamais reçu, en rupture ou négatif (RG-048), et
 * normal. Les afficher pareil ferait passer un stock négatif — qui signale
 * deux appareils ayant vendu la même dernière unité — pour un stock ordinaire.
 */
function CelluleStock({ produit }: { produit: ProduitGroupe }): ReactNode {
  // L'API ramène l'absence de stock à zéro : « jamais reçu » et « en rupture »
  // s'affichent donc pareil, et c'est la même information pour le vendeur.
  if (produit.stockDisponible <= 0) {
    return <Badge statut="echu" libelle={String(produit.stockDisponible)} />;
  }

  const sousSeuil =
    produit.seuilReapprovisionnement !== undefined &&
    produit.seuilReapprovisionnement > 0 &&
    produit.stockDisponible <= produit.seuilReapprovisionnement;

  return sousSeuil ? (
    <Badge statut="credit" libelle={String(produit.stockDisponible)} />
  ) : (
    <Nombre valeur={produit.stockDisponible} />
  );
}
